import React, { useState, useCallback, useMemo } from 'react';
import { PayoffInputs, FilingStatus, LumpSum } from '../types';
import { usePayoffVsInvestCalculator } from '../hooks/usePayoffVsInvestCalculator';
import { InputCard, InputRow } from './InputCard';
import { SALT_CAPS } from '../constants';
import { PayoffResultsDisplay } from './PayoffResultsDisplay';
import { PayoffDataTable } from './PayoffDataTable';
import { InfoTooltip } from './InfoTooltip';
import { TaxInfo } from './TaxInfo';
import { generatePayoffPdf } from '../utils/pdfGenerator';

const oneYearAgo = () => {
    const d = new Date();
    d.setFullYear(d.getFullYear() - 1);
    return d.toISOString().split('T')[0];
};

export const PayoffCalculator: React.FC = () => {
  const [inputs, setInputs] = useState<PayoffInputs>({
    originalLoanAmount: 750000,
    interestRate: 6.25,
    originationDate: oneYearAgo(),
    loanTerm: 30,
    homeValue: 950000,
    appreciation: 3,
    extraPayment: 2500,
    initialReturnRate: 4,
    initialReturnPeriod: 10,
    longTermReturnRate: 6,
    taxRateOnGains: 20,
    filingStatus: FilingStatus.MARRIED_FILING_JOINTLY,
    marginalTaxRate: 24,
    otherItemizedDeductions: 0,
    lumpSums: [],
    isARM: false,
    armFixedTerm: 5,
    armSubsequentRate: 7.25,
  });
  
  const [saltWarning, setSaltWarning] = useState<boolean>(false);
  const [submittedInputs, setSubmittedInputs] = useState<PayoffInputs | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const results = usePayoffVsInvestCalculator(submittedInputs);
  const saltCapForStatus = useMemo(() => SALT_CAPS[inputs.filingStatus], [inputs.filingStatus]);

  const handleCalculate = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setSubmittedInputs(null);
    setTimeout(() => {
      setSubmittedInputs(inputs);
      setTimeout(() => setIsLoading(false), 300);
    }, 50);
  }, [inputs]);
  
  const handleReset = () => {
    setSubmittedInputs(null);
  }

  const handleDownloadPdf = () => {
    if (submittedInputs && results) {
      generatePayoffPdf(submittedInputs, results);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    setInputs(prev => {
        let finalValue: string | number | boolean;

        if (type === 'checkbox') {
            finalValue = (e.target as HTMLInputElement).checked;
        } else if (['originalLoanAmount', 'extraPayment', 'homeValue', 'otherItemizedDeductions'].includes(name)) {
          finalValue = Number(value.replace(/[^0-9]/g, '')) || 0;
        } else if (type === 'number') {
            const numValue = parseFloat(value) || 0;
            if (name === 'interestRate' || name === 'armSubsequentRate') finalValue = Math.max(0, Math.min(30, numValue));
            else if (name === 'loanTerm' || name === 'armFixedTerm') finalValue = Math.max(1, Math.min(50, numValue));
            else if (name === 'appreciation') finalValue = Math.max(-10, Math.min(20, numValue));
            else if (['initialReturnRate', 'longTermReturnRate'].includes(name)) finalValue = Math.max(-20, Math.min(50, numValue));
            else if (['marginalTaxRate', 'taxRateOnGains'].includes(name)) finalValue = Math.max(0, Math.min(100, numValue));
            else finalValue = Math.max(0, numValue);
        } else {
          finalValue = value;
        }
        
        const newInputs = { ...prev, [name]: finalValue };
        setSaltWarning(newInputs.otherItemizedDeductions > SALT_CAPS[newInputs.filingStatus]);
        return newInputs;
    });
    
    if (submittedInputs) setSubmittedInputs(null);
  };

  const addLumpSum = () => {
    setInputs(prev => ({
        ...prev,
        lumpSums: [...prev.lumpSums, { amount: 25000, yearsFromNow: 2 }]
    }));
    if (submittedInputs) setSubmittedInputs(null);
  };

  const updateLumpSum = (index: number, field: keyof LumpSum, value: number) => {
      setInputs(prev => {
          const newLumpSums = [...prev.lumpSums];
          const numValue = Math.max(0, value);
          newLumpSums[index] = { ...newLumpSums[index], [field]: numValue };
          return { ...prev, lumpSums: newLumpSums };
      });
      if (submittedInputs) setSubmittedInputs(null);
  };

  const removeLumpSum = (index: number) => {
      setInputs(prev => ({
          ...prev,
          lumpSums: prev.lumpSums.filter((_, i) => i !== index)
      }));
      if (submittedInputs) setSubmittedInputs(null);
  };

  const inputClass = "w-full p-2 border rounded bg-white dark:bg-slate-700 dark:border-slate-600 dark:text-white transition-colors focus:ring-2 focus:ring-bp-light-blue outline-none";

  return (
    <>
      <header className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-bp-slate dark:text-white">Pay Off Mortgage Early vs. Invest</h1>
          <p className="text-lg text-gray-600 dark:text-gray-300 mt-2">Should you use extra cash to pay down your mortgage or invest it?</p>
      </header>

      <main>
        <form onSubmit={handleCalculate}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="flex flex-col gap-6">
                <InputCard title="Your Current Mortgage">
                    <InputRow label="Original Loan Amount ($)" id="originalLoanAmount">
                        <input type="text" inputMode="numeric" id="originalLoanAmount" name="originalLoanAmount" value={inputs.originalLoanAmount.toLocaleString('en-US')} onChange={handleChange} className={inputClass} required />
                    </InputRow>
                    <InputRow label="Interest Rate (%)" id="interestRate">
                        <div className="flex items-center">
                            <input type="number" step="0.001" id="interestRate" name="interestRate" value={inputs.interestRate} onChange={handleChange} className={inputClass} min="0" max="30" required />
                            <InfoTooltip text="Enter your current interest rate. If you have an ARM, this is the initial or current rate." />
                        </div>
                    </InputRow>
                    
                    {/* ARM Toggle & Inputs */}
                    <div className="py-2">
                        <div className="flex items-center mb-2">
                            <input 
                                type="checkbox" 
                                id="isARM" 
                                name="isARM" 
                                checked={inputs.isARM} 
                                onChange={handleChange}
                                className="w-4 h-4 text-bp-dark-blue border-gray-300 rounded focus:ring-bp-light-blue"
                            />
                            <label htmlFor="isARM" className="ml-2 font-semibold text-gray-700 dark:text-gray-300">Is this an Adjustable Rate Mortgage (ARM)?</label>
                        </div>
                        
                        {inputs.isARM && (
                            <div className="pl-6 border-l-2 border-blue-200 dark:border-blue-800 space-y-3 bg-blue-50 dark:bg-blue-900/10 p-3 rounded-r-lg">
                                <InputRow label="Fixed Rate Period (Years)" id="armFixedTerm">
                                     <div className="flex items-center">
                                        <input type="number" step="1" id="armFixedTerm" name="armFixedTerm" value={inputs.armFixedTerm} onChange={handleChange} className={inputClass} min="1" max="30" />
                                        <InfoTooltip text="How many years is the rate fixed for? (e.g. 5 for a 5/1 ARM)" />
                                     </div>
                                </InputRow>
                                <InputRow label="Est. Future Rate (%)" id="armSubsequentRate">
                                    <div className="flex items-center">
                                        <input type="number" step="0.125" id="armSubsequentRate" name="armSubsequentRate" value={inputs.armSubsequentRate} onChange={handleChange} className={inputClass} min="0" max="30" />
                                        <InfoTooltip text="What do you expect the rate to adjust to after the fixed period ends?" />
                                    </div>
                                </InputRow>
                            </div>
                        )}
                    </div>

                    <InputRow label="Origination Date" id="originationDate">
                        <input type="date" id="originationDate" name="originationDate" value={inputs.originationDate} onChange={handleChange} className={inputClass} required />
                    </InputRow>
                    <InputRow label="Loan Term (Years)" id="loanTerm">
                        <input type="number" step="1" id="loanTerm" name="loanTerm" value={inputs.loanTerm} onChange={handleChange} className={inputClass} min="1" max="50" required />
                    </InputRow>
                </InputCard>
                 <InputCard title="Home Assumptions">
                    <InputRow label="Current Home Value ($)" id="homeValue">
                         <input type="text" inputMode="numeric" id="homeValue" name="homeValue" value={inputs.homeValue.toLocaleString('en-US')} onChange={handleChange} className={inputClass} required />
                    </InputRow>
                    <InputRow label="Annual Appreciation (%)" id="appreciation">
                        <input type="number" step="0.1" id="appreciation" name="appreciation" value={inputs.appreciation} onChange={handleChange} className={inputClass} min="-10" max="20" required />
                    </InputRow>
                </InputCard>
                 <InputCard title="Your Payoff vs. Invest Strategy">
                    <InputRow label="Extra Monthly Payment ($)" id="extraPayment">
                        <div className="flex items-center">
                        <input type="text" inputMode="numeric" id="extraPayment" name="extraPayment" value={inputs.extraPayment.toLocaleString('en-US')} onChange={handleChange} className={inputClass} required />
                        <InfoTooltip text="Enter the extra amount you could consistently pay towards your mortgage OR invest each month."/>
                        </div>
                    </InputRow>
                    <hr className="my-2 border-gray-200 dark:border-slate-600"/>
                    <p className="font-semibold text-gray-700 dark:text-gray-300 mb-2">Lump Sum Payments (Optional)</p>
                    {inputs.lumpSums.map((lump, index) => (
                        <div key={index} className="grid grid-cols-3 gap-2 items-center">
                            <input 
                                type="text"
                                inputMode="numeric"
                                aria-label="Lump sum amount"
                                value={lump.amount.toLocaleString('en-US')}
                                onChange={(e) => updateLumpSum(index, 'amount', Number(e.target.value.replace(/[^0-9]/g, '')))}
                                className={`${inputClass} col-span-1`}
                            />
                             <div className="relative col-span-1">
                                <input 
                                    type="number" 
                                    aria-label="Years from now"
                                    value={lump.yearsFromNow}
                                    onChange={(e) => updateLumpSum(index, 'yearsFromNow', parseFloat(e.target.value))}
                                    className={`${inputClass} pr-16`}
                                    min="0"
                                />
                                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400 text-sm">years</span>
                            </div>
                            <button type="button" onClick={() => removeLumpSum(index)} className="text-red-500 hover:text-red-700 dark:hover:text-red-400 font-semibold">
                                Remove
                            </button>
                        </div>
                    ))}
                    <button type="button" onClick={addLumpSum} className="w-full bg-gray-200 hover:bg-gray-300 dark:bg-slate-700 dark:hover:bg-slate-600 text-gray-800 dark:text-gray-200 font-bold py-2 px-4 rounded-lg transition-colors">
                        + Add Lump Sum
                    </button>
                </InputCard>
            </div>
            
            <div className="flex flex-col gap-6">
                <InputCard title="Tax Assumptions">
                    <InputRow label="Filing Status" id="filingStatus">
                        <select id="filingStatus" name="filingStatus" value={inputs.filingStatus} onChange={handleChange} className={inputClass}>
                        {Object.values(FilingStatus).map(status => (
                            <option key={status} value={status}>{status}</option>
                        ))}
                        </select>
                    </InputRow>
                    <InputRow label="Marginal Tax Rate (%)" id="marginalTaxRate">
                        <input type="number" id="marginalTaxRate" name="marginalTaxRate" value={inputs.marginalTaxRate} onChange={handleChange} className={inputClass} min="0" max="100" required />
                    </InputRow>
                    <InputRow label="Other Deductions ($)" id="otherItemizedDeductions">
                      <div className="flex items-center">
                        <input type="text" inputMode="numeric" id="otherItemizedDeductions" name="otherItemizedDeductions" value={inputs.otherItemizedDeductions.toLocaleString('en-US')} onChange={handleChange} className={inputClass} required />
                        <InfoTooltip text="Enter other annual itemized deductions. Note: The State and Local Tax (SALT) deduction cap depends on your filing status and increases by 1% annually under current tax law. This helps determine if you'd benefit from the mortgage interest deduction." />
                      </div>
                    </InputRow>
                     {saltWarning && (
                        <div className="text-sm text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/30 p-2 rounded-md -mt-2 border border-amber-200 dark:border-amber-800">
                            Heads up: The State and Local Tax (SALT) deduction is capped at {saltCapForStatus.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 })} for your filing status. Your entry may overstate your tax savings.
                        </div>
                      )}
                    {results && <TaxInfo itemizationInfo={results.summary.itemizationInfo} />}
                </InputCard>
                 <InputCard title="Investment Assumptions">
                     <InputRow label="Projected Initial Return (%)" id="initialReturnRate">
                        <div className="flex items-center">
                            <input type="number" step="0.1" id="initialReturnRate" name="initialReturnRate" value={inputs.initialReturnRate} onChange={handleChange} className={inputClass} min="-20" max="50" required />
                        </div>
                    </InputRow>
                    <InputRow label="For First (Years)" id="initialReturnPeriod">
                        <div className="flex items-center">
                            <input type="number" step="1" id="initialReturnPeriod" name="initialReturnPeriod" value={inputs.initialReturnPeriod} onChange={handleChange} className={inputClass} min="0" max="50" required />
                        </div>
                    </InputRow>
                    <InputRow label="Long-Term Return Rate (%)" id="longTermReturnRate">
                        <div className="flex items-center">
                            <input type="number" step="0.1" id="longTermReturnRate" name="longTermReturnRate" value={inputs.longTermReturnRate} onChange={handleChange} className={inputClass} min="-20" max="50" required />
                        </div>
                    </InputRow>
                    <InputRow label="Tax Rate on Gains (%)" id="taxRateOnGains">
                        <div className="flex items-center">
                            <input type="number" id="taxRateOnGains" name="taxRateOnGains" value={inputs.taxRateOnGains} onChange={handleChange} className={inputClass} min="0" max="100" />
                            <InfoTooltip text="The capital gains tax rate applied to earnings in your investment accounts upon withdrawal."/>
                        </div>
                    </InputRow>
                </InputCard>
            </div>
          </div>

          <div className="mt-6 text-center">
              <button type="submit" className="bg-bp-dark-blue hover:bg-bp-slate text-white font-bold py-3 px-8 rounded-lg text-lg transition-colors shadow-lg disabled:bg-gray-400" disabled={isLoading}>
                {isLoading ? 'Calculating...' : 'Calculate'}
              </button>
               {results && (
                <>
                  <button type="button" onClick={handleReset} className="ml-4 bg-gray-500 hover:bg-gray-600 dark:bg-slate-600 dark:hover:bg-slate-500 text-white font-bold py-3 px-8 rounded-lg text-lg transition-colors shadow-lg">
                    Reset
                  </button>
                   <button type="button" onClick={handleDownloadPdf} className="ml-4 bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-8 rounded-lg text-lg transition-colors shadow-lg">
                    Download PDF Report
                  </button>
                </>
              )}
          </div>
        </form>

        <div className="mt-8">
            <PayoffResultsDisplay results={results} isLoading={isLoading} />
        </div>
        
        {results && !isLoading && (
            <PayoffDataTable data={results.monthlyData} />
        )}
      </main>
    </>
  );
};